import {
  Activity,
  AudioLines,
  Book,
  Bot,
  Building,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarCog,
  CreditCard,
  LayoutDashboard,
  MessageSquareCode,
  MessagesSquare,
  Phone,
  PhoneCall,
  PhoneForwarded,
  PhoneOutgoing,
  Plug,
  Settings,
  Sparkles,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { FileRouteTypes } from '@/routeTree.gen'

export type AppPath = FileRouteTypes['to']

export interface NavItem {
  path: AppPath
  title: string
  icon: LucideIcon
  /** Permission required to see this item and access its route */
  permission: string
  /** Additional path prefixes that belong to this section (for isActive + route guard) */
  activePrefixes?: string[]
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'navigation',
    label: 'Navigation',
    items: [
      { path: '/dashboard', title: 'Dashboard', icon: LayoutDashboard, permission: 'call_log:view' },
      { path: '/activities', title: 'Aktivität', icon: Activity, permission: 'event:view' },
      { path: '/calls', title: 'Anrufe', icon: Phone, permission: 'call_log:view' },
      { path: '/chats', title: 'Chats', icon: MessagesSquare, permission: 'widget_key:view' },
      { path: '/callbacks', title: 'Tickets', icon: Ticket, permission: 'callback:view' },
      { path: '/contacts', title: 'Kontakte', icon: Users, permission: 'contact:view' },
      { path: '/kampangen', title: 'Kampangen', icon: PhoneOutgoing, permission: 'call:initiate' },
      { path: '/bookings', title: 'Buchungen', icon: Calendar, permission: 'event:view' },
    ],
  },
  {
    id: 'konfiguration',
    label: 'Konfiguration',
    items: [
      { path: '/assistant', title: 'AudavoGPT', icon: Sparkles, permission: 'agent:view' },
      { path: '/agent-configuration', title: 'Agent', icon: Bot, permission: 'agent:view', activePrefixes: ['/agent/'] },
      { path: '/appointments-configuration', title: 'Termine', icon: CalendarCheck, permission: 'event_type:view' },
      { path: '/calendar-configuration', title: 'Kalender', icon: CalendarCog, permission: 'calendar:view' },
      { path: '/transfer-destinations', title: 'Weiterleitungen', icon: PhoneForwarded, permission: 'transfer:view' },
      { path: '/chat-widgets', title: 'Chat Widgets', icon: MessageSquareCode, permission: 'widget_key:view' },
      { path: '/booking-keys', title: 'Buchungsseiten', icon: CalendarClock, permission: 'booking_key:view' },
      { path: '/integrations', title: 'Integrationen', icon: Plug, permission: 'integration:view' },
      { path: '/knowledge-base', title: 'Wissensdatenbank', icon: Book, permission: 'knowledge:view' },
      { path: '/settings', title: 'Einstellungen', icon: Settings, permission: 'user:view' },
      { path: '/organization', title: 'Organisation', icon: Building, permission: 'organization:view' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { path: '/admin/billing', title: 'Abrechnung', icon: CreditCard, permission: 'billing:admin' },
      { path: '/admin/organizations', title: 'Organisationen', icon: Building2, permission: 'organization:admin' },
      { path: '/admin/telephony', title: 'Telefonie', icon: PhoneCall, permission: 'telephony:admin' },
      { path: '/admin/voices', title: 'Stimmen', icon: AudioLines, permission: 'voice:admin' },
    ],
  },
]

/** Routes that are not sidebar items but still require a permission */
const EXTRA_ROUTE_PERMISSIONS: Array<{ prefix: string; permission: string }> = [
  { prefix: '/analytics', permission: 'analytics:view' },
]

/**
 * Look up the permission required for a pathname.
 * Returns null for routes without a permission requirement (e.g. OAuth callbacks).
 */
export function requiredPermissionFor(pathname: string): string | null {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return item.permission
      }
      if (item.activePrefixes?.some((prefix) => pathname.startsWith(prefix))) {
        return item.permission
      }
    }
  }
  for (const { prefix, permission } of EXTRA_ROUTE_PERMISSIONS) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return permission
    }
  }
  return null
}

/**
 * First sidebar route the user is allowed to open, in display order.
 * Used as landing page and as redirect target for denied routes.
 */
export function firstAllowedPath(hasPermission: (permission: string) => boolean): AppPath | null {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((i) => hasPermission(i.permission))
    if (item) return item.path
  }
  return null
}
